<template>
  <div class="create-or-edit-task-component" :class="{loaded: ' loaded'}">
    <div class="panel panel-primary panel-stretch">
      <div class="panel-heading">
        Taak
      </div>

      <div class="panel-body" v-if="!loaded">
        <a-icon type="loading"/>
        Het formulier wordt geladen...
      </div>

      <a-form-model v-else :model="form" :colon="false"
                    :label-col="labelCol"
                    :wrapper-col="wrapperCol">
        <div class="panel-body">
          <a-form-model-item label="Template">
            <a-select class="task-input-template mt4" :disabled="loading" v-model="template"
                      placeholder="Selecteer een taak template"
                      @select="handleSelectTemplate">
              <a-select-option v-for="(template, key) in options.templates" :key="key" :value="key">
                {{ template.name }}
              </a-select-option>
            </a-select>
          </a-form-model-item>

          <a-form-model-item label="Type" :has-feedback="true"
                             :validate-status="hasValidationErrors(validation.type)" :help="validation.type">
            <a-select class="task-input-type mt4" :disabled="loading" v-model="form.type"
                      placeholder="Selecteer het type taak"
                      @change="clearValidationErrors('type')">
              <a-select-option v-for="(value, key) in options.types" :key="key" :value="value">
                {{ translateTaskType(value) }}
              </a-select-option>
            </a-select>
          </a-form-model-item>

          <a-form-model-item label="Beknopte omschrijving" :has-feedback="true"
                             :validate-status="hasValidationErrors(validation.content)" :help="validation.content">
            <a-input class="task-input-content mt4" :disabled="loading" v-model="form.content"
                     placeholder="Omschrijf de taak"
                     @change="clearValidationErrors('content')"/>
          </a-form-model-item>

          <a-form-model-item label="Notitie toevoegen">
            <a-textarea class="task-input-description mt4" :disabled="loading" v-model="form.description" :rows="5"/>
          </a-form-model-item>

          <a-form-model-item label="Toewijzen aan gebruiker(s)" :has-feedback="true"
                             :validate-status="hasValidationErrors(validation.assigned_users)"
                             :help="validation.assigned_users">
            <users-select class="task-input-users mt4" :disabled="loading"
                          v-model="form.assigned_users"
                          @change="clearAssignedToValidationErrors"/>
          </a-form-model-item>

          <a-form-model-item label="Toewijzen aan afdeling(en)" :has-feedback="true"
                             :validate-status="hasValidationErrors(validation.assigned_departments)"
                             :help="validation.assigned_departments">
            <user-departments-select class="task-input-departments mt4" :disabled="loading"
                                     v-model="form.assigned_departments"
                                     @change="clearAssignedToValidationErrors"/>
          </a-form-model-item>

          <a-form-model-item label="Labels">
            <product-labels-select class="task-input-labels mt4" :disabled="loading"
                                   v-model="form.assigned_product_labels"
                                   product-category="task"/>
          </a-form-model-item>

          <a-form-model-item label="Datum" :has-feedback="true" :validate-status="hasValidationErrors(validation.date)"
                             :help="validation.date">
            <date-picker-with-suggestions class="task-input-date mt4 mb9" :disabled="loading" v-model="form.date"
                                          @change="handleSelectDate"
                                          :allow-clear="true"
                                          :nullable-date="true"
                                          :date-picker-format="DateFormatEnum.DISPLAY_DATE"/>
          </a-form-model-item>

          <a-form-model-item label="Klant" :has-feedback="true"
                             :validate-status="hasValidationErrors(validation.customer_id)"
                             :help="validation.customer_id">
            <customer-select class="task-input-customer mt4" :disabled="loading" v-model="form.customer_id"
                             @change="handleCustomerChange"/>
          </a-form-model-item>

          <a-form-model-item label="Categorie" :has-feedback="true"
                             :validate-status="hasValidationErrors(validation.category_id)"
                             :help="validation.category_id">
            <category-select class="task-input-category mt4" :disabled="loading"
                             v-model="form.category_id" @change="clearValidationErrors('category_id')"/>
          </a-form-model-item>

          <a-form-model-item label="Prioriteit" :has-feedback="true"
                             :validate-status="hasValidationErrors(validation.priority)"
                             :help="validation.priority">
            <a-radio-group name="task-input-priority"
                           class="task-input-priority mt4" :disabled="loading" v-model="form.priority"
                           button-style="solid" @change="clearValidationErrors('priority')"
                           :defaultValue="TaskPriorityEnum.NORMAL">
              <a-radio-button v-for="(value, key) in options.priorities" :key="key" :value="value">
                {{ translateTaskPriority(value) }}
              </a-radio-button>
            </a-radio-group>
          </a-form-model-item>

          <a-form-model-item label="Status">
            <a-radio-group name="task-input-status"
                           class="task-input-status mt4" :disabled="loading" v-model="form.status"
                           button-style="solid" @change="handleTaskFinished"
                           :defaultValue="TaskStatusEnum.ACTIVE">
              <a-radio-button :value="TaskStatusEnum.ACTIVE">Actief</a-radio-button>
              <a-radio-button :value="TaskStatusEnum.COMPLETED">Afgerond</a-radio-button>
            </a-radio-group>
          </a-form-model-item>

          <a-form-model-item label="Zichtbaar voor klant">
            <a-switch class="task-input-visibility mt10" :disabled="loading" v-model="form.visible_for_customer"/>
          </a-form-model-item>

          <a-form-model-item label="Ook als notitie aanmaken" v-if="this.isNewTask">
            <a-popover placement="right">
              <template slot="content">
                <p>Notitie aanmaken kan als er een klant is geselecteerd.</p>
              </template>
              <a-switch v-model="form.create_note" :disabled="this.hasNoCustomer"
                        :class="[this.hasNoCustomer ? 'mt5' : 'mt10']"/>
            </a-popover>
          </a-form-model-item>
        </div>

        <div class="panel-footer">
          <a-form-model-item :wrapper-col="{span: 24}">
            <div class="flex-row justify-content-between">
              <a-button class="task-cancel-button" @click="handleCancel" :disabled="loading">
                Annuleren
              </a-button>
              <div class="flex-row justify-content-end">
                <LoadingIcon v-if="loading" class="animate-spin icon-size-lg"></LoadingIcon>
                <a-button class="task-save-button" type="primary" @click="handleSubmit" :disabled="loading">
                  Opslaan
                </a-button>
              </div>
            </div>
          </a-form-model-item>
        </div>
      </a-form-model>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

import DateFormatEnum from '~/enums/DateFormatEnum';
import DynamicDateEnum from '~/enums/DynamicDateEnum';
import TaskPriorityEnum from '~/enums/TaskPriorityEnum';
import TaskStatusEnum from '~/enums/TaskStatusEnum';
import TaskTypeEnum from '~/enums/TaskTypeEnum';

import categoryApi from '~/api/category';
import taskApi from '~/api/task';
import taskTemplateApi from '~/api/task_template';
import userApi from '~/api/user';

export default {
  name: "CreateOrEditTaskComponent",
  props: {
    taskId: {
      type: [Number, String],
      required: false,
    },
    categoryId: {
      type: [Number, String],
      default: null,
      required: false,
    },
    customerId: {
      type: [Number, String],
      default: null,
      required: false,
    },
    instanceType: {
      type: String,
      default: null,
      required: false,
    },
    instanceId: {
      type: [Number, String],
      default: null,
      required: false,
    },
    redirectUrl: {
      type: String,
      required: false,
    },
    defaultAssignedUsers: {
      type: Array,
      required: false,
    },
    defaultAssignedDepartments: {
      type: Array,
      required: false,
    },
    type: {
      type: Number,
      default: TaskTypeEnum.GENERAL_TASK
    }
  },
  data() {
    return {
      DateFormatEnum,
      TaskStatusEnum,
      TaskPriorityEnum,
      loading: true,
      loaded: false,
      form: {},
      userId: null,
      template: null,

      options: {
        categories: {},
        dates: DynamicDateEnum,
        priorities: TaskPriorityEnum,
        templates: {},
        types: TaskTypeEnum,
      },
      validation: {
        assigned_departments: null,
        assigned_users: null,
        category_id: null,
        content: null,
        customer_id: null,
        date: null,
        priority: null,
        type: null,
      },
      defaultType: TaskTypeEnum.GENERAL_TASK
    }
  },
  mounted() {
    this.defaultType = this.translateTaskType(this.type)
    Promise.all([
      categoryApi.getCategories().then(response => {
        this.options.categories = response.data
      }).catch(() => {
        this.$notification['error']({
          message: 'Er ging iets mis',
          description: 'Het is niet gelukt om categorieën te laden.',
        });
      }),
      taskTemplateApi.get(['users', 'departments', 'product_labels']).then(response => {
        this.options.templates = response.data
      }).catch(() => {
        this.$notification['error']({
          message: 'Er ging iets mis',
          description: 'Het is niet gelukt om templates te laden.',
        });
      }),
      userApi.getMe().then(response => {
        this.userId = response.data.id
      }).catch(() => {
        this.$notification['error']({
          message: 'Er ging iets mis',
          description: 'Het is niet gelukt om de gebruiker te laden.',
        });
      }),
      this.initTask()
    ])
        .then()
        .catch()
        .finally(() => {
          this.loading = false;
          this.loaded = true;
        });
  },
  computed: {
    hasTaskId() {
      return typeof this.taskId !== 'undefined' && this.taskId !== null && this.taskId !== '';
    },
    isNewTask() {
      return !this.hasTaskId;
    },
    hasNoCustomer() {
      return this.form.customer_id === null;
    },
    labelCol() {
      switch (this.$mq) {
        case 'mobile':
          return {span: 6};
        case 'tablet':
          return {span: 6};
        case 'laptop':
          return {span: 6};
        case 'desktop':
          return {span: 6};
      }
    },
    wrapperCol() {
      switch (this.$mq) {
        case 'mobile':
          return {span: 18};
        case 'tablet':
          return {span: 18};
        case 'laptop':
          return {span: 18};
        case 'desktop':
          return {span: 18};
      }
    },
  },
  methods: {
    initTask() {
      return new Promise((resolve, reject) => {
        if (this.hasTaskId) {
          taskApi.getById(this.taskId).then(response => {
            this.setForm(response.data);
            resolve();
          }).catch(reject);
        } else {
          this.setForm();
          resolve();
        }
      });
    },
    setForm(task = null) {
      this.form = {
        type: task ? task.type : this.translateTaskTypeToEnum(this.defaultType),
        content: task ? task.content : null,
        description: task ? task.description : null,
        assigned_users: task ? task.users.data.map(user => user.id) : this.defaultAssignedUsers,
        assigned_departments: task ? task.departments.data.map(department => department.id) : this.defaultAssignedDepartments,
        assigned_product_labels: task ? task.product_labels.data.map(product_label => product_label.id) : [],
        date: task ?
            Object.values(DynamicDateEnum).includes(task.date) ?
                this.setDateByDynamicDate(task.date) :
                task.date
            : null,
        customer_id: task ? task.customer_id : this.customerId,
        category_id: task ? task.category_id : this.categoryId === null ? null : parseInt(this.categoryId),
        priority: task ? task.priority : TaskPriorityEnum.NORMAL,
        status: task ? task.status : TaskStatusEnum.ACTIVE,
        finished_by: task ? task.finished_by : null,
        finished_at: task && (task.finished_at !== null) ?
            moment(task.finished_at, DateFormatEnum.DB_ENTRY).format(DateFormatEnum.DB_ENTRY)
            : null,
        visible_for_customer: task ? !!task.visible_for_customer : false,
        user_id: task ? task.user_id : null,
        instance_type: task ? task.instance_type : this.instanceType,
        instance_id: task ? task.instance_id : this.instanceId,
        create_note: false,
      }
    },
    handleSelectTemplate(value) {
      this.$confirm({
        content: 'Reeds ingevulde velden kunnen worden overschreven. Wil je doorgaan?',
        okText: 'Ja',
        cancelText: 'Annuleren',
        onOk: () => {
          const template = this.options.templates[value];

          for (let attribute in template) {
            if (['users', 'departments', 'product_labels'].includes(attribute)) {
              this.form['assigned_' + attribute] = template[attribute].data.map(item => item.id);
            } else if (attribute === 'date') {
              this.form[attribute] = this.setDateByDynamicDate(template[attribute]);
            } else {
              this.form[attribute] = template[attribute];
            }
          }
        },
        onCancel: () => {
          this.template = null;
        },
      });
    },
    setDateByDynamicDate(dynamicDate) {
      switch (dynamicDate) {
        case DynamicDateEnum.TODAY:
          return moment().format(DateFormatEnum.DATE);
        case DynamicDateEnum.TOMORROW:
          return moment().add(1, 'days').format(DateFormatEnum.DATE);
        case DynamicDateEnum.NEXT_WEEK:
          return moment().add(1, 'weeks').format(DateFormatEnum.DATE);
        case DynamicDateEnum.NEXT_TWO_WEEKS:
          return moment().add(2, 'weeks').format(DateFormatEnum.DATE);
        case DynamicDateEnum.NEXT_THREE_WEEKS:
          return moment().add(3, 'weeks').format(DateFormatEnum.DATE);
        case DynamicDateEnum.NEXT_MONTH:
          return moment().add(1, 'months').format(DateFormatEnum.DATE);
        case DynamicDateEnum.NEXT_YEAR:
          return moment().add(1, 'years').format(DateFormatEnum.DATE);
        case DynamicDateEnum.NEXT_THREE_YEARS:
          return moment().add(3, 'years').format(DateFormatEnum.DATE);
        case DynamicDateEnum.NEXT_FIVE_YEARS:
          return moment().add(5, 'years').format(DateFormatEnum.DATE);
        default:
          return null;
      }
    },
    translateTaskPriority(priority) {
      switch (priority) {
        case TaskPriorityEnum.CRITICAL:
          return "Kritiek";
        case TaskPriorityEnum.IMPORTANT:
          return "Belangrijk";
        case TaskPriorityEnum.NORMAL:
          return "Normaal";
        case TaskPriorityEnum.INSIGNIFICANT:
          return "Onbelangrijk";
      }
    },
    translateTaskType(type) {
      switch (type) {
        case TaskTypeEnum.CALL_BACK_REQUEST:
          return "Terugbelverzoek";
        case TaskTypeEnum.CHECK_TASK:
          return "Controle taak";
        case TaskTypeEnum.CORRESPOND_TASK:
          return "Correspondentie taak";
        case TaskTypeEnum.BIRTHDAY_TASK:
          return "Verjaardag";
        case TaskTypeEnum.GENERAL_TASK:
          return "Algemene taak";
        case TaskTypeEnum.MAINTENANCE_PRIVATE:
          return "Onderhoud particulier";
        case TaskTypeEnum.MAINTENANCE_BUSINESS:
          return "Onderhoud zakelijk";
      }
    },
    translateTaskTypeToEnum(type) {
      switch (type) {
        case "Terugbelverzoek":
          return TaskTypeEnum.CALL_BACK_REQUEST;
        case "Controle taak":
          return TaskTypeEnum.CHECK_TASK;
        case "Correspondentie taak":
          return TaskTypeEnum.CORRESPOND_TASK;
        case "Verjaardag":
          return TaskTypeEnum.BIRTHDAY_TASK;
        case "Algemene taak":
          return TaskTypeEnum.GENERAL_TASK;
        case "Onderhoud particulier":
          return TaskTypeEnum.MAINTENANCE_PRIVATE;
        case "Onderhoud zakelijk":
          return TaskTypeEnum.MAINTENANCE_BUSINESS;
      }
    },
    handleSelectDate(value) {
      this.clearValidationErrors('date');
      this.form.date = (moment.isMoment(value) ? moment(value).format(DateFormatEnum.DATE) : null);
    },
    handleSelectUsers(value) {
      this.form.assigned_users = value;
      this.clearAssignedToValidationErrors();
    },
    handleTaskFinished(event) {
      this.clearValidationErrors('status');
      if (event.target.value === TaskStatusEnum.ACTIVE) {
        this.form.finished_by = null;
        this.form.finished_at = null;
      } else {
        userApi.getMe().then(response => {
          this.form.finished_by = response.data.id;
          this.form.finished_at = new moment().format(DateFormatEnum.DB_ENTRY);
        }).catch(() => {
          this.$notification['error']({
            message: 'Er ging iets mis',
            description: 'Het is niet gelukt om de gebruiker te laden.',
          });
        });
      }
    },

    hasValidationErrors(input) {
      return input !== null ? 'error' : null;
    },
    clearValidationErrors(field) {
      this.validation[field] = null;
    },
    clearAssignedToValidationErrors() {
      this.clearValidationErrors('assigned_departments');
      this.clearValidationErrors('assigned_users');
    },
    updateValidation(data) {
      for (const key in data) {
        this.validation[key] = data[key];
      }
    },
    handleSubmit() {
      this.loading = true;
      if (!this.form.user_id) {
        this.form.user_id = this.userId;
      }
      this.hasTaskId ? this.updateTask() : this.storeTask();
    },
    storeTask() {
      this.form.id = null;
      taskApi.store(this.form).then(() => {
        this.$notification['success']({
          message: "Succes",
          description: "De taak is opgeslagen."
        });
        if (this.redirectUrl) {
          window.location.href = this.redirectUrl;
        } else {
          this.$emit('store', this.form);
        }
      }).catch((error) => {
        if (typeof error.response !== 'undefined' && typeof error.response.data !== 'undefined' && typeof error.response.data.error !== 'undefined' && typeof error.response.data.error.errors !== 'undefined') {
          this.updateValidation(error.response.data.error.errors);
        } else {
          this.$notification['error']({
            message: "Er ging iets mis",
            description: "De taak is niet opgeslagen."
          });
        }
      }).finally(() => {
        this.loading = false;
      })
    },
    updateTask() {
      this.form.id = this.taskId;
      taskApi.update(this.form).then(() => {
        this.$notification['success']({
          message: "Succes",
          description: "De taak is opgeslagen."
        });
        if (this.redirectUrl) {
          window.location.href = this.redirectUrl;
        } else {
          this.$emit('update', this.form);
        }
      }).catch((error) => {
        if (typeof error.response !== 'undefined' && typeof error.response.data !== 'undefined' && typeof error.response.data.error !== 'undefined' && typeof error.response.data.error.errors !== 'undefined') {
          this.updateValidation(error.response.data.error.errors);
        } else {
          this.$notification['error']({
            message: "Er ging iets mis",
            description: "De taak is niet opgeslagen."
          });
        }
      }).finally(() => {
        this.loading = false;
      })
    },
    handleCancel() {
      if (this.redirectUrl) {
        window.location.href = this.redirectUrl;
      } else {
        this.$emit('cancel');
      }
    },
    handleCustomerChange() {
      this.clearValidationErrors('customer_id');
      if (this.form.customer_id === null) {
        this.form.create_note = false;
      }
    },
  },
}
</script>

<style scoped>
.mt4 {
  margin-top: 4px;
}

.mt5 {
  margin-top: 5px;
}

.mt10 {
  margin-top: 10px;
}

.mb9 {
  margin-bottom: 9px;
}

.task-input-users {
  background-color: white;
}

.task-cancel-button {
  min-width: 150px;
}

.task-save-button {
  margin-left: 10px;
  float: right;
  min-width: 150px;
}
</style>

<style>
.has-error.has-feedback .ant-radio-group + .ant-form-item-children-icon {
  right: -30px;
}
</style>
